import type { RealmId } from "@chuzi/shared/types";

export interface DirectorAvatarProps {
  /** Stable id used as the seed — same id always produces the same avatar. */
  seed: string;
  /** Realm chooses the icon style: constellation for cosmos, sapling for wilds. */
  realm: RealmId | null;
  /** Pixel size of the rendered SVG; defaults to 100% so it fills its container. */
  size?: number | "100%";
  /** Override the foreground tint (defaults to realm-specific accent). */
  color?: string;
}

/**
 * Procedural director avatar. Renders an SVG seeded by the user id so each
 * director gets a unique-but-stable image — a constellation in cosmos, a
 * sapling in wilds — without any backend asset dependency. Used in the
 * top-left director circle and the profile modal.
 */
export function DirectorAvatar({
  seed,
  realm,
  size = "100%",
  color,
}: DirectorAvatarProps) {
  const realmKey: RealmId = realm ?? "cosmos";
  const fg = color ?? (realmKey === "wilds" ? "#bce0a4" : "#cfe1ff");

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={realmKey === "wilds" ? "Sapling avatar" : "Constellation avatar"}
      style={{ display: "block" }}
    >
      {realmKey === "wilds"
        ? renderSapling(seedToRng(seed), fg)
        : renderConstellation(seedToRng(seed), fg)}
    </svg>
  );
}

// ── Constellation (cosmos) ────────────────────────────────────────────────

function renderConstellation(rng: () => number, fg: string) {
  const pointCount = 5 + Math.floor(rng() * 3); // 5–7 stars
  const points: { x: number; y: number; r: number }[] = [];
  // Reject-sample inside a circle to keep the constellation centered.
  while (points.length < pointCount) {
    const x = 18 + rng() * 64;
    const y = 18 + rng() * 64;
    const dx = x - 50;
    const dy = y - 50;
    if (dx * dx + dy * dy > 32 * 32) continue;
    const tooClose = points.some(
      (p) => (p.x - x) ** 2 + (p.y - y) ** 2 < 14 * 14,
    );
    if (tooClose) continue;
    points.push({ x, y, r: 1.6 + rng() * 1.6 });
  }

  // Edges: chain through points in a deterministic-but-jittered order so the
  // shape feels like a real constellation rather than a regular polygon.
  const order = [...points.keys()].sort((a, b) => {
    const pa = points[a];
    const pb = points[b];
    return Math.atan2(pa.y - 50, pa.x - 50) - Math.atan2(pb.y - 50, pb.x - 50);
  });
  const edges: [number, number][] = [];
  for (let i = 0; i < order.length - 1; i++) {
    edges.push([order[i], order[i + 1]]);
  }
  // Add one cross-link for visual interest.
  if (order.length >= 4) {
    edges.push([order[0], order[Math.floor(order.length / 2)]]);
  }

  return (
    <g>
      {edges.map(([a, b], i) => (
        <line
          key={`e-${i}`}
          x1={points[a].x}
          y1={points[a].y}
          x2={points[b].x}
          y2={points[b].y}
          stroke={fg}
          strokeOpacity={0.5}
          strokeWidth={0.8}
          strokeLinecap="round"
        />
      ))}
      {points.map((p, i) => (
        <circle key={`p-${i}`} cx={p.x} cy={p.y} r={p.r} fill={fg} />
      ))}
    </g>
  );
}

// ── Sapling (wilds) ───────────────────────────────────────────────────────

function renderSapling(rng: () => number, fg: string) {
  // Trunk: gentle curve from base up.
  const sway = (rng() - 0.5) * 14;
  const trunk = `M 50 88 Q ${50 + sway} 60, 50 32`;

  // Leaves: 5–7 ellipses dotted around the upper trunk, varying hue around fg.
  const leafCount = 5 + Math.floor(rng() * 3);
  const leaves: {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
    rot: number;
  }[] = [];
  for (let i = 0; i < leafCount; i++) {
    const t = i / Math.max(1, leafCount - 1);
    const cy = 32 + (1 - t) * 24;
    const side = i % 2 === 0 ? 1 : -1;
    const cx = 50 + side * (8 + rng() * 6) + sway * (1 - t);
    leaves.push({
      cx,
      cy,
      rx: 7 + rng() * 3,
      ry: 4 + rng() * 2,
      rot: side * (20 + rng() * 30),
    });
  }
  // Crown leaf at the top.
  leaves.push({ cx: 50, cy: 28, rx: 9, ry: 5, rot: 0 });

  return (
    <g>
      <path
        d={trunk}
        stroke="#5a4530"
        strokeWidth={3.2}
        fill="none"
        strokeLinecap="round"
      />
      {leaves.map((l, i) => (
        <ellipse
          key={`l-${i}`}
          cx={l.cx}
          cy={l.cy}
          rx={l.rx}
          ry={l.ry}
          fill={fg}
          fillOpacity={0.85}
          transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
        />
      ))}
    </g>
  );
}

// ── Seeding ───────────────────────────────────────────────────────────────

function seedToRng(seed: string): () => number {
  // FNV-1a over the seed string to a 32-bit integer, then mulberry32.
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  let s = h >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
